import { useCallback, useEffect, useState, type SetStateAction } from "react";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";

const TEXT_LOCK_STORAGE_KEY_PREFIX = "takeoff-measurement-text-locked";
const TEXT_FONT_SIZE_STORAGE_KEY_PREFIX = "takeoff-measurement-text-font-size";
export const DEFAULT_TEXT_FONT_SIZE = 13;

interface MeasurementTextSettingsScope {
  jobId: Id<"takeoffPilotJobs"> | undefined;
  planDocumentId?: Id<"takeoffPilotPlanDocuments">;
  planRevisionId?: Id<"projectDocumentRevisions">;
  persistedTextLocked?: boolean;
  persistedTextFontSize?: number;
}

function getStorageScopeKey(args: MeasurementTextSettingsScope): string | null {
  if (!args.jobId) {
    return null;
  }
  return `${args.jobId}:${args.planDocumentId ?? "none"}:${args.planRevisionId ?? "none"}`;
}

function getLockStorageKey(scopeKey: string): string {
  return `${TEXT_LOCK_STORAGE_KEY_PREFIX}:${scopeKey}`;
}

function getFontSizeStorageKey(scopeKey: string): string {
  return `${TEXT_FONT_SIZE_STORAGE_KEY_PREFIX}:${scopeKey}`;
}

/**
 * Hook to manage the text lock setting for measurement annotations.
 * When locked, text keeps its selected size relative to the PDF and scales with zoom.
 * When unlocked (default), text stays at a fixed screen size regardless of zoom.
 * Server-persisted settings take precedence; localStorage remains as a legacy fallback.
 */
export function useMeasurementTextLock(scope: MeasurementTextSettingsScope): {
  textLocked: boolean;
  toggleTextLock: () => void;
  setTextLock: (locked: SetStateAction<boolean>) => void;
  textFontSize: number;
  setTextFontSize: (size: number) => void;
  settingsLoaded: boolean;
} {
  const [textLocked, setTextLockedState] = useState<boolean>(false);
  const [textFontSize, setTextFontSizeState] = useState<number>(DEFAULT_TEXT_FONT_SIZE);
  const [loadedScopeKey, setLoadedScopeKey] = useState<string | null>(null);
  const scopeKey = getStorageScopeKey(scope);

  useEffect(() => {
    if (!scopeKey) {
      setTextLockedState(false);
      setTextFontSizeState(DEFAULT_TEXT_FONT_SIZE);
      setLoadedScopeKey(null);
      return;
    }

    const scopeJobId = scope.jobId;
    if (!scopeJobId) {
      setTextLockedState(false);
      setTextFontSizeState(DEFAULT_TEXT_FONT_SIZE);
      setLoadedScopeKey(null);
      return;
    }
    const legacyJobLockKey = `${TEXT_LOCK_STORAGE_KEY_PREFIX}:${scopeJobId}`;
    const legacyJobFontSizeKey = `${TEXT_FONT_SIZE_STORAGE_KEY_PREFIX}:${scopeJobId}`;

    if (scope.persistedTextLocked !== undefined) {
      setTextLockedState(scope.persistedTextLocked);
    } else {
      try {
        const storedLockValue =
          localStorage.getItem(getLockStorageKey(scopeKey)) ??
          localStorage.getItem(legacyJobLockKey);
        setTextLockedState(storedLockValue === "true");
      } catch {
        setTextLockedState(false);
      }
    }

    if (
      scope.persistedTextFontSize !== undefined &&
      Number.isFinite(scope.persistedTextFontSize) &&
      scope.persistedTextFontSize > 0
    ) {
      setTextFontSizeState(scope.persistedTextFontSize);
    } else {
      try {
        const storedFontSizeValue =
          localStorage.getItem(getFontSizeStorageKey(scopeKey)) ??
          localStorage.getItem(legacyJobFontSizeKey);
        const parsedFontSize = storedFontSizeValue ? parseFloat(storedFontSizeValue) : NaN;
        if (Number.isFinite(parsedFontSize) && parsedFontSize > 0) {
          setTextFontSizeState(parsedFontSize);
        } else {
          setTextFontSizeState(DEFAULT_TEXT_FONT_SIZE);
        }
      } catch {
        setTextFontSizeState(DEFAULT_TEXT_FONT_SIZE);
      }
    }
    setLoadedScopeKey(scopeKey);
  }, [scope.persistedTextFontSize, scope.persistedTextLocked, scopeKey]);

  const setTextLock = useCallback(
    (locked: SetStateAction<boolean>) => {
      setTextLockedState((previous) => {
        if (!scopeKey) {
          return previous;
        }
        const nextLocked =
          typeof locked === "function" ? locked(previous) : locked;
        try {
          localStorage.setItem(getLockStorageKey(scopeKey), String(nextLocked));
        } catch {
          // Keep the session usable even if persistence is unavailable.
        }
        return nextLocked;
      });
    },
    [scopeKey]
  );

  const setTextFontSize = useCallback(
    (size: number) => {
      if (!Number.isFinite(size) || size <= 0) {
        return;
      }
      setTextFontSizeState((previous) => {
        if (!scopeKey) {
          return previous;
        }
        try {
          localStorage.setItem(getFontSizeStorageKey(scopeKey), String(size));
        } catch {
          // Keep the session usable even if persistence is unavailable.
        }
        return size;
      });
    },
    [scopeKey]
  );

  const toggleTextLock = useCallback(() => {
    setTextLock((previous) => !previous);
  }, [setTextLock]);

  return {
    textLocked,
    toggleTextLock,
    setTextLock,
    textFontSize,
    setTextFontSize,
    settingsLoaded: scopeKey !== null && loadedScopeKey === scopeKey,
  };
}
